import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useState, useContext, useEffect } from "react";
import { CommonContext } from "../../contexts/CommonContext";
import { useParams, useNavigate } from "react-router-dom";
import ComponentLoader from "../../components/ComponentLoader";
import { Helmet } from "react-helmet";
import NavHeader from "../../components/NavHeader";
import ImageSlider from "../../components/ImageSlider";
import { motion } from "framer-motion";
import Grid from "@mui/material/Unstable_Grid2";
import PilotCartFAB from "../../components/PilotCartFAB";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  addToPilotCart,
  updatePilotCartQuantity,
  getProductQuantityInCart,
  generateCartItemId,
} from "../../utils/pilotUtils";
import { normalizePilotCustomizationsForOrder } from "../../utils/pilotCustomizationShape";

function ProductDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isDesktop, showSnackbar } = useContext(CommonContext);

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [customizationsOpen, setCustomizationsOpen] = useState(false);
  const [selectedCustomizations, setSelectedCustomizations] = useState({});
  const [cartQuantity, setCartQuantity] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${process.env.REACT_APP_PILOT_URL}/api/products/${id}`, {
          method: "GET",
          headers: {
            "accept": "application/json",
            "content-type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error("Failed to fetch product");
        }

        const data = await response.json();
        
        if (data.success && data.data) {
          setProduct(data.data);
          // Set first variant as default
          if (data.data.variants && data.data.variants.length > 0) {
            setSelectedVariant(data.data.variants[0]);
          }
          // Initialize default customizations
          if (data.data.customizations) {
            const defaults = {};
            Object.entries(data.data.customizations).forEach(([key, customization]) => {
              if (customization.options && customization.options.length > 0) {
                if (customization.uiType === "checkbox") {
                  // For checkboxes, initialize with array of default options
                  const defaultOptions = customization.options.filter(opt => opt.isDefault);
                  if (defaultOptions.length > 0) {
                    defaults[key] = defaultOptions;
                  } else {
                    // No defaults, start with empty array
                    defaults[key] = [];
                  }
                } else {
                  // For radio/select, use single default option
                  const defaultOption = customization.options.find(opt => opt.isDefault) || customization.options[0];
                  if (defaultOption) {
                    defaults[key] = defaultOption;
                  }
                }
              }
            });
            setSelectedCustomizations(defaults);
          }
          
          // Check if product is already in cart (will be checked in useEffect)
        } else {
          setError("Product not found");
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError(err.message || "Error loading product. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  // Calculate final price including customizations
  const calculateFinalPrice = () => {
    if (!product) return 0;
    const basePrice = selectedVariant?.price || product.livePrice || product.price || 0;
    let additionalPrice = 0;
    
    Object.values(selectedCustomizations).forEach((option) => {
      if (Array.isArray(option)) {
        // Handle checkbox arrays - sum all selected options
        option.forEach((opt) => {
          if (opt && opt.additionalPrice) {
            additionalPrice += parseFloat(opt.additionalPrice) || 0;
          }
        });
      } else if (option && option.additionalPrice) {
        // Handle single selection (radio/select)
        additionalPrice += parseFloat(option.additionalPrice) || 0;
      }
    });
    
    return parseFloat(basePrice) + additionalPrice;
  };

  const currentPrice = calculateFinalPrice();
  const baseProductPrice = product
    ? parseFloat(selectedVariant?.price || product.livePrice || product.price || 0) || 0
    : 0;
  const currentWeight = selectedVariant?.weight || product?.netWeight || product?.grossWeight;

  const customizationsForStorage = () =>
    product?.customizations
      ? normalizePilotCustomizationsForOrder(
          product.customizations,
          selectedCustomizations
        )
      : { ...selectedCustomizations };

  // Check cart quantity when variant or customizations change
  useEffect(() => {
    if (product) {
      checkCartQuantity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, selectedVariant, selectedCustomizations, currentPrice]);

  const checkCartQuantity = async () => {
    if (!product) return;
    
    const cartProduct = {
      productId: product.id || "ERROR",
      name: product.name,
      imageUrl: product.image || (product.images && product.images[0]) || "",
      quantity: 1,
      price: currentPrice,
      variant: selectedVariant || {},
      customizations: customizationsForStorage(),
    };
    
    const quantity = await getProductQuantityInCart(cartProduct);
    setCartQuantity(quantity);
  };

  if (loading) {
    return (
      <Box
        sx={{
          padding: isDesktop ? "4vw 10vw" : "4vw",
          marginTop: "7vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #fff5e8 50%, #fef3e2 100%)",
          minHeight: "100vh",
        }}
      >
        <ComponentLoader />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box
        sx={{
          padding: isDesktop ? "4vw 10vw" : "4vw",
          marginTop: "7vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #fff5e8 50%, #fef3e2 100%)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <NavHeader />
        <Box sx={{ color: "#a4243d", fontSize: "18px", mb: 2, textAlign: "center" }}>
          {error || "Product not found"}
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate("/pilot/products")}
          sx={{
            background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
            color: "white",
            fontWeight: "700",
            padding: "12px 32px",
            borderRadius: "12px",
            textTransform: "none",
            fontFamily: "RobotoSlab",
          }}
        >
          Back to Products
        </Button>
      </Box>
    );
  }


  const handleCustomizationChange = (customizationKey, option) => {
    setSelectedCustomizations((prev) => {
      const newCustomizations = { ...prev };
      
      // Check if it's a radio (single selection) or checkbox (multiple selection)
      const customization = product.customizations[customizationKey];
      if (customization.uiType === "checkbox") {
        // For checkbox, allow multiple selections - store as array
        const currentSelections = Array.isArray(newCustomizations[customizationKey])
          ? newCustomizations[customizationKey]
          : newCustomizations[customizationKey]
          ? [newCustomizations[customizationKey]]
          : [];
        
        // Check if option is already selected
        const optionIndex = currentSelections.findIndex(opt => opt.id === option.id);
        
        if (optionIndex >= 0) {
          // Option is already selected, remove it (unless required and it's the only one)
          if (!customization.required || currentSelections.length > 1) {
            currentSelections.splice(optionIndex, 1);
            if (currentSelections.length === 0) {
              delete newCustomizations[customizationKey];
            } else {
              newCustomizations[customizationKey] = currentSelections;
            }
          }
        } else {
          // Option is not selected, add it
          newCustomizations[customizationKey] = [...currentSelections, option];
        }
      } else {
        // For radio/select, replace the selection
        newCustomizations[customizationKey] = option;
      }
      
      return newCustomizations;
    });
  };

  const handleAddToCart = async () => {
    if (!selectedVariant && product.variants && product.variants.length > 0) {
      // If no variant selected but variants exist, show error
      return;
    }

    // Check if required customizations are selected
    if (product.customizations) {
      const requiredCustomizations = Object.entries(product.customizations).filter(
        ([_, customization]) => customization.required
      );
      
      for (const [key, customization] of requiredCustomizations) {
        if (!selectedCustomizations[key]) {
          // Show error for missing required customization
          return;
        }
      }
    }

    // Prepare product data in the specified format
    const cartProduct = {
      productId: product.id || "ERROR",
      name: product.name,
      imageUrl: product.image || (product.images && product.images[0]) || "",
      quantity: 1,
      price: currentPrice,
      variant: selectedVariant || {},
      customizations: customizationsForStorage(),
    };

    // Add to cart (store in localStorage/Preferences)
    const result = await addToPilotCart(cartProduct);
    
    if (result.success) {
      setCartQuantity(cartQuantity + 1);
      setCustomizationsOpen(false);
      showSnackbar("Product added to cart successfully!", "success");
    }
  };

  const handleQuantityChange = async (change) => {
    if (!selectedVariant && product.variants && product.variants.length > 0) {
      return;
    }

    const cartProduct = {
      productId: product.id || "ERROR",
      name: product.name,
      imageUrl: product.image || (product.images && product.images[0]) || "",
      quantity: 1,
      price: currentPrice,
      variant: selectedVariant || {},
      customizations: customizationsForStorage(),
    };

    const result = await updatePilotCartQuantity(cartProduct, change);
    
    if (result.success) {
      const newQuantity = cartQuantity + change;
      setCartQuantity(Math.max(0, newQuantity));
      
      if (change > 0) {
        showSnackbar("Quantity increased", "success");
      } else if (newQuantity === 0) {
        showSnackbar("Product removed from cart", "success");
      } else {
        showSnackbar("Quantity decreased", "success");
      }
    }
  };

  return (
    <Box
      sx={{
        padding: isDesktop ? "4vw 10vw" : "4vw",
        marginTop: "7vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #fff5e8 50%, #fef3e2 100%)",
        minHeight: "100vh",
        marginBottom: "7vh",
      }}
    >
      <Helmet>
        <title>{product.name} - Product Details</title>
        <meta name="description" content={product.description || product.name} />
      </Helmet>
      <NavHeader />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Button
          onClick={() => navigate("/pilot/products")}
          sx={{
            mb: 3,
            color: "#a4243d",
            fontWeight: "600",
            "&:hover": {
              background: "rgba(164, 36, 61, 0.1)",
            },
          }}
        >
          ← Back to Products
        </Button>

        <Grid container spacing={4}>
          {/* Image Section */}
          <Grid xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  minHeight: "400px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#FFF5E8",
                  padding: "20px",
                }}
              >
                {product.images && product.images.length > 1 ? (
                  <ImageSlider imgUrls={product.images} />
                ) : (product.images && product.images.length === 1) || product.image ? (
                  <img
                    src={product.images?.[0] || product.image}
                    alt={product.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      maxHeight: "500px",
                    }}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/400x400?text=No+Image";
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                    }}
                  >
                    No Image Available
                  </Box>
                )}
              </Box>
            </motion.div>
          </Grid>

          {/* Details Section */}
          <Grid xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Box
                sx={{
                  background: "white",
                  borderRadius: "20px",
                  padding: "32px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                }}
              >
                {/* Product Name */}
                <Box
                  sx={{
                    fontWeight: "700",
                    fontSize: isDesktop ? "32px" : "28px",
                    fontFamily: "Foregen",
                    textTransform: "uppercase",
                    color: "#2d3436",
                    marginBottom: "16px",
                    letterSpacing: "1px",
                  }}
                >
                  {product.name}
                </Box>

                {/* Category */}
                {product.category && (
                  <Box
                    sx={{
                      fontSize: "14px",
                      color: "#a4243d",
                      fontWeight: "600",
                      marginBottom: "12px",
                      fontFamily: "RobotoSlab",
                    }}
                  >
                    Category: {product.category}
                  </Box>
                )}

                {/* Description */}
                {product.description && (
                  <Box
                    sx={{
                      fontSize: "16px",
                      color: "#6c757d",
                      lineHeight: 1.6,
                      marginBottom: "24px",
                      fontFamily: "RobotoSlab",
                    }}
                  >
                    {product.description}
                  </Box>
                )}

                {/* Metadata */}
                <Box sx={{ marginBottom: "24px" }}>
                  {product.metadata?.style && (
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#6c757d",
                        marginBottom: "8px",
                        fontFamily: "RobotoSlab",
                      }}
                    >
                      <strong>Style:</strong> {product.metadata.style}
                    </Box>
                  )}
                  {product.metadata?.aka && (
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#6c757d",
                        marginBottom: "8px",
                        fontFamily: "RobotoSlab",
                      }}
                    >
                      <strong>Also known as:</strong> {product.metadata.aka}
                    </Box>
                  )}
                  {product.metadata?.age && (
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#6c757d",
                        marginBottom: "8px",
                        fontFamily: "RobotoSlab",
                      }}
                    >
                      <strong>Age:</strong> {product.metadata.age}
                    </Box>
                  )}
                  {product.metadata?.shelfLife && (
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#6c757d",
                        marginBottom: "8px",
                        fontFamily: "RobotoSlab",
                      }}
                    >
                      <strong>Shelf Life:</strong> {product.metadata.shelfLife}
                    </Box>
                  )}
                </Box>

                {/* Variants Selection */}
                {product.variants && product.variants.length > 0 && (
                  <Box sx={{ marginBottom: "24px" }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#2d3436",
                        fontFamily: "RobotoSlab",
                        marginRight: "8px",
                      }}
                    >
                      Size/Weight : 
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: "inline",
                        fontSize: "16px",
                        color: "#6c757d",
                        fontFamily: "RobotoSlab",
                        lineHeight: 1.6,
                      }}
                    >
                      {product.variants
                        .filter((v) => v.visibleToCustomer !== false && v.isActive !== false)
                        .map((variant, index, arr) => {
                          const selected = selectedVariant?.sku === variant.sku;
                          return (
                            <React.Fragment key={variant.sku}>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => setSelectedVariant(variant)}
                                aria-pressed={selected}
                                sx={{
                                  border: "none",
                                  background: "none",
                                  padding: 0,
                                  margin: 0,
                                  cursor: "pointer",
                                  font: "inherit",
                                  fontFamily: "RobotoSlab",
                                  fontSize: "inherit",
                                  color: selected ? "#a4243d" : "#6c757d",
                                  fontWeight: selected ? 700 : 400,
                                  textDecoration: selected ? "underline" : "none",
                                  textDecorationColor: "rgba(164, 36, 61, 0.45)",
                                  textUnderlineOffset: "4px",
                                  "&:hover": {
                                    color: "#a4243d",
                                  },
                                  "&:focus-visible": {
                                    outline: "2px solid rgba(164, 36, 61, 0.45)",
                                    outlineOffset: "2px",
                                    borderRadius: "2px",
                                  },
                                }}
                              >
                                {variant.weight} — ₹{variant.price}
                              </Box>
                              {index < arr.length - 1 && (
                                <Box
                                  component="span"
                                  sx={{
                                    color: "#ced4da",
                                    userSelect: "none",
                                    mx: "10px",
                                  }}
                                  aria-hidden
                                >
                                  ·
                                </Box>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </Box>
                  </Box>
                )}

                {/* Price and Add to Cart */}
                <Box
                  sx={{
                    paddingTop: "24px",
                    borderTop: "1px solid #e9ecef",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        fontSize: "14px",
                        color: "#6c757d",
                        marginBottom: "4px",
                        fontFamily: "RobotoSlab",
                      }}
                    >
                      {currentWeight && `Weight: ${currentWeight}`}
                    </Box>
                    <Box
                      sx={{
                        fontWeight: "800",
                        fontSize: "36px",
                        fontFamily: "RobotoSlabBold",
                        background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      ₹{baseProductPrice}
                    </Box>
                  </Box>

                  {cartQuantity > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        background: "white",
                        borderRadius: "12px",
                        border: "2px solid #a4243d",
                        padding: "8px",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => handleQuantityChange(-1)}
                        sx={{
                          minWidth: "40px",
                          width: "40px",
                          height: "40px",
                          borderRadius: "8px",
                          borderColor: "#a4243d",
                          color: "#a4243d",
                          fontSize: "20px",
                          fontWeight: "700",
                          padding: 0,
                          "&:hover": {
                            background: "rgba(164, 36, 61, 0.1)",
                            borderColor: "#a4243d",
                          },
                        }}
                      >
                        −
                      </Button>
                      <Box
                        sx={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#2d3436",
                          minWidth: "40px",
                          textAlign: "center",
                          fontFamily: "RobotoSlab",
                        }}
                      >
                        {cartQuantity}
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={() => handleQuantityChange(1)}
                        sx={{
                          minWidth: "40px",
                          width: "40px",
                          height: "40px",
                          borderRadius: "8px",
                          borderColor: "#a4243d",
                          color: "#a4243d",
                          fontSize: "20px",
                          fontWeight: "700",
                          padding: 0,
                          "&:hover": {
                            background: "rgba(164, 36, 61, 0.1)",
                            borderColor: "#a4243d",
                          },
                        }}
                      >
                        +
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={product.status !== "active"}
                      sx={{
                        background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                        color: "white",
                        fontWeight: "700",
                        fontSize: "16px",
                        padding: "14px 32px",
                        borderRadius: "12px",
                        textTransform: "none",
                        fontFamily: "RobotoSlab",
                        boxShadow: "0 4px 15px rgba(164, 36, 61, 0.3)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #8a1e33 0%, #e55555 100%)",
                          boxShadow: "0 6px 20px rgba(164, 36, 61, 0.4)",
                        },
                        "&:disabled": {
                          opacity: 0.5,
                        },
                      }}
                      onClick={() => {
                        // Check if product has customizations
                        if (product.customizations && Object.keys(product.customizations).length > 0) {
                          setCustomizationsOpen(true);
                        } else {
                          // No customizations, add directly to cart
                          handleAddToCart();
                        }
                      }}
                    >
                      {product.status === "active" ? "Add to Cart" : "Unavailable"}
                    </Button>
                  )}
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </motion.div>

      {/* Customizations Modal */}
      <Dialog
        open={customizationsOpen}
        onClose={() => setCustomizationsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <Box
            sx={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#2d3436",
              fontFamily: "RobotoSlab",
            }}
          >
            Customize Your Order
          </Box>
          <IconButton
            onClick={() => setCustomizationsOpen(false)}
            sx={{
              color: "#6c757d",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          {product && product.customizations && Object.entries(product.customizations).map(([key, customization]) => (
            <FormControl
              key={key}
              component="fieldset"
              sx={{
                marginBottom: "24px",
                width: "100%",
              }}
            >
              <FormLabel
                component="legend"
                sx={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#2d3436",
                  marginBottom: "12px",
                  fontFamily: "RobotoSlab",
                }}
              >
                {customization.displayName}
                {customization.required && (
                  <span style={{ color: "#a4243d", marginLeft: "4px" }}>*</span>
                )}
              </FormLabel>
              
              {customization.description && (
                <Box
                  sx={{
                    fontSize: "14px",
                    color: "#6c757d",
                    marginBottom: "12px",
                    fontFamily: "RobotoSlab",
                  }}
                >
                  {customization.description}
                </Box>
              )}

              {customization.uiType === "checkbox" ? (
                <Box>
                  {customization.options && customization.options.map((option) => (
                    <FormControlLabel
                      key={option.id || option.name}
                      control={
                        <Checkbox
                          checked={
                            Array.isArray(selectedCustomizations[key])
                              ? selectedCustomizations[key].some(opt => opt.id === option.id)
                              : selectedCustomizations[key]?.id === option.id
                          }
                          onChange={() => handleCustomizationChange(key, option)}
                          sx={{
                            color: "#a4243d",
                            "&.Mui-checked": {
                              color: "#a4243d",
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <span>{option.name}</span>
                          {option.additionalPrice > 0 && (
                            <span style={{ color: "#a4243d", fontWeight: "600" }}>
                              (+₹{option.additionalPrice})
                            </span>
                          )}
                        </Box>
                      }
                      sx={{
                        marginBottom: "8px",
                        fontFamily: "RobotoSlab",
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <RadioGroup
                  value={selectedCustomizations[key]?.id || ""}
                  onChange={(e) => {
                    const option = customization.options.find(
                      (opt) => opt.id === e.target.value
                    );
                    if (option) {
                      handleCustomizationChange(key, option);
                    }
                  }}
                >
                  {customization.options && customization.options.map((option) => (
                    <FormControlLabel
                      key={option.id || option.name}
                      value={option.id || option.name}
                      control={
                        <Radio
                          sx={{
                            color: "#a4243d",
                            "&.Mui-checked": {
                              color: "#a4243d",
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <span>{option.name}</span>
                          {option.additionalPrice > 0 && (
                            <span style={{ color: "#a4243d", fontWeight: "600" }}>
                              (+₹{option.additionalPrice})
                            </span>
                          )}
                        </Box>
                      }
                      sx={{
                        marginBottom: "8px",
                        fontFamily: "RobotoSlab",
                        padding: "8px",
                        borderRadius: "8px",
                        backgroundColor: selectedCustomizations[key]?.id === option.id ? "rgba(164, 36, 61, 0.05)" : "transparent",
                        transition: "background-color 0.2s",
                      }}
                    />
                  ))}
                </RadioGroup>
              )}
            </FormControl>
          ))}

          {/* Price Summary */}
          <Box
            sx={{
              marginTop: "24px",
              padding: "16px",
              background: "#f8f9fa",
              borderRadius: "12px",
              border: "1px solid #e9ecef",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Box sx={{ fontSize: "16px", color: "#6c757d", fontFamily: "RobotoSlab" }}>
                Base Price:
              </Box>
              <Box sx={{ fontSize: "16px", fontWeight: "600", fontFamily: "RobotoSlab" }}>
                ₹{selectedVariant?.price || product.livePrice || product.price || 0}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "20px 24px",
            borderTop: "1px solid #e9ecef",
            gap: 2,
          }}
        >
          <Button
            onClick={() => setCustomizationsOpen(false)}
            sx={{
              color: "#6c757d",
              fontWeight: "600",
              fontFamily: "RobotoSlab",
            }}
          >
            Cancel
          </Button>
          {cartQuantity > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                background: "white",
                borderRadius: "12px",
                border: "2px solid #a4243d",
                padding: "8px",
              }}
            >
              <Button
                variant="outlined"
                onClick={() => handleQuantityChange(-1)}
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  borderColor: "#a4243d",
                  color: "#a4243d",
                  fontSize: "20px",
                  fontWeight: "700",
                  padding: 0,
                  "&:hover": {
                    background: "rgba(164, 36, 61, 0.1)",
                    borderColor: "#a4243d",
                  },
                }}
              >
                −
              </Button>
              <Box
                sx={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#2d3436",
                  minWidth: "40px",
                  textAlign: "center",
                  fontFamily: "RobotoSlab",
                }}
              >
                {cartQuantity}
              </Box>
              <Button
                variant="outlined"
                onClick={() => handleQuantityChange(1)}
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  borderColor: "#a4243d",
                  color: "#a4243d",
                  fontSize: "20px",
                  fontWeight: "700",
                  padding: 0,
                  "&:hover": {
                    background: "rgba(164, 36, 61, 0.1)",
                    borderColor: "#a4243d",
                  },
                }}
              >
                +
              </Button>
            </Box>
          ) : (
            <Button
              onClick={handleAddToCart}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                color: "white",
                fontWeight: "700",
                padding: "12px 32px",
                borderRadius: "12px",
                textTransform: "none",
                fontFamily: "RobotoSlab",
                boxShadow: "0 4px 15px rgba(164, 36, 61, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #8a1e33 0%, #e55555 100%)",
                  boxShadow: "0 6px 20px rgba(164, 36, 61, 0.4)",
                },
              }}
            >
              Add to Cart
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <PilotCartFAB />
    </Box>
  );
}

export default ProductDetail;
